/**
 * Fixed-income (bond) fund utility helpers — CUSIP enrichment, label
 * derivation, and bond name parsing. Used by FundDetailPage to annotate
 * live holdings returned by the holdings API.
 *
 * The CUSIP-keyed `BOND_HOLDINGS_MAP` enrichment table is intentionally
 * empty: bond holdings come from the live ingestion feed. Populate when
 * wiring live enrichment data.
 */
// ──────────────────────────────────────────────────────────────────────
// CUSIP-keyed reference enrichment helpers
// ──────────────────────────────────────────────────────────────────────

export interface BondReferenceData {
  issuerLabel?: string;
  fullName?: string;
  kind?: "treasury" | "corporate" | "agency" | "cash";
  country?: string;
  spRating?: string;
  moodysRating?: string;
  maturity?: string;
  coupon?: string;
  sector?: string;
}

export function normalizeCusip(cusip: string | null | undefined): string {
  return (cusip ?? "").replace(/\s+/g, "").toUpperCase();
}

export const LABEL_RULES: ReadonlyArray<{
  pattern: RegExp;
  label: string;
  kind: NonNullable<BondReferenceData["kind"]>;
}> = [
  { pattern: /^united states treasury bill\b/i, label: "T-Bill", kind: "treasury" },
  { pattern: /^united states treasury note\/bond\b/i, label: "T-Note", kind: "treasury" },
  { pattern: /^united states treasury note\b/i, label: "T-Note", kind: "treasury" },
  { pattern: /^united states treasury bond\b/i, label: "T-Bond", kind: "treasury" },
  { pattern: /^united states treasury inflation/i, label: "TIPS", kind: "treasury" },
  { pattern: /^cash\b/i, label: "Cash", kind: "cash" },
  { pattern: /^first american government obligations fund\b/i, label: "Cash", kind: "cash" },
];

/**
 * Bare-issuer → canonical ticker map. Intentionally empty in v0.
 *
 * When populated via admin / DDB globals (future), entries take precedence over
 * the truncated-name fallback. Until then, `deriveLabelFromName` shows the
 * truncated issuer name — NOT a fake 4-letter abbreviation.
 */
export const CORPORATE_ISSUER_TICKER: Record<string, string> = {};

const NAME_TAIL_RE = /\s+\d+(?:\.\d+)?%\s+\d{1,2}\/\d{1,2}\/\d{4}\s*$/;

export function deriveLabelFromName(name: string | null | undefined): {
  label: string;
  kind: NonNullable<BondReferenceData["kind"]>;
} {
  const safe = (name ?? "").trim();
  if (!safe) return { label: "—", kind: "corporate" };

  for (const rule of LABEL_RULES) {
    if (rule.pattern.test(safe)) {
      return { label: rule.label, kind: rule.kind };
    }
  }

  const residue = safe.replace(NAME_TAIL_RE, "").trim();
  const corpTicker = CORPORATE_ISSUER_TICKER[residue];
  if (corpTicker) return { label: corpTicker, kind: "corporate" };

  // Catch treasury names not covered by LABEL_RULES (e.g. "US TREASURY", "U.S. TREASURY")
  if (/treasury/i.test(residue)) {
    return { label: "U.S. Treasury", kind: "treasury" };
  }

  // Show truncated issuer name — NOT a fake 4-letter ticker abbreviation.
  // "United Healthcare Group" → "United Healthcare " (18 chars, trailing space trimmed below).
  return { label: residue.slice(0, 18).trim() || "—", kind: "corporate" };
}

/**
 * CUSIP → descriptive enrichment map. Empty until live bond holdings enrichment
 * data is wired; the fixed-income holdings table renders its empty state.
 */
export const BOND_HOLDINGS_MAP: Readonly<Record<string, BondReferenceData>> = Object.freeze({});

export function enrichBondHolding<T extends { cusip?: string | null }>(
  h: T,
): T & Partial<BondReferenceData> {
  const ref = BOND_HOLDINGS_MAP[normalizeCusip(h.cusip)];
  return ref ? { ...ref, ...h } : h;
}

export interface ParsedBondNameFields {
  coupon: string | null;
  maturity: string | null;
}

const COUPON_AND_MATURITY_RE = /(\d+(?:\.\d+)?)\s*%\s+(\d{1,2}\/\d{1,2}\/\d{4})\s*$/;

export function parseBondNameFields(name: string | null | undefined): ParsedBondNameFields {
  const safe = (name ?? "").trim();
  const m = COUPON_AND_MATURITY_RE.exec(safe);
  if (!m) return { coupon: null, maturity: null };
  const couponNum = Number.parseFloat(m[1]!);
  if (!Number.isFinite(couponNum)) {
    return { coupon: null, maturity: m[2] ?? null };
  }
  return {
    coupon: `${couponNum.toFixed(3)}%`,
    maturity: m[2] ?? null,
  };
}
